import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import { useAuthContext } from "~/contexts/auth";
import useSubscriptions from "~/hooks/use-subscriptions";
import { HttpService } from "~/services/http";
import { ApiConstants } from "~/shared/api/api-constants.class";
import { ClientUtils } from "~/utils/client-utils";
import crossCountryUtils from "~/utils/cross-country-utils";
import loyaltyUtils from "~/utils/loyalty-utils";

import Button from "../common/button";

enum ENABLED_OS {
  ANDROID = "android",
  IOS = "ios",
  OTHER = "other",
}

export default function CaptainWalletButton() {
  const { userSubscriptions } = useSubscriptions();
  const { user } = useAuthContext();
  const router = useRouter();
  const countryCode = crossCountryUtils.getCurrentCountryCode(router).toLowerCase();

  const t = useTranslations();

  const userId = user?.ID;
  const loyaltyId = loyaltyUtils.getLoyaltySubscription(userSubscriptions)?.UserIdentifier;

  const { data: walletUrl } = useQuery({
    queryKey: ["walletUrl", userId],
    staleTime: Infinity,
    queryFn: async () =>
      await HttpService.$http<{ url: string }>({
        url: ApiConstants.endpoints.captainWalletUrl,
        queryParams: {
          userId: `${userId}`,
          loyaltyId: `${loyaltyId}`,
        },
      }),
    enabled: !!loyaltyId,
  });

  const os = useMemo(() => {
    const uaOs = ClientUtils.getOS()?.toLowerCase();
    if (uaOs === "ios") {
      return ENABLED_OS.IOS;
    } else if (uaOs?.startsWith("android")) {
      return ENABLED_OS.ANDROID;
    }
    return ENABLED_OS.OTHER;
  }, []);

  const isOther = os === ENABLED_OS.OTHER;
  return walletUrl?.url ? (
    <Button
      variant={isOther ? "primary" : undefined}
      onClick={() => {
        window.location.href = walletUrl.url;
      }}
    >
      {isOther ? (
        <span>{t("loyalty.wallet.bindbutton.label")}</span>
      ) : (
        <Image
          unoptimized
          height={40}
          width={180}
          aria-hidden="true"
          alt={!isOther ? t("loyalty.wallet.bindbutton.alt." + os) : ""}
          src={`/next/assets/wallet/${os}/${countryCode}.svg`}
        />
      )}
    </Button>
  ) : (
    <></>
  );
}
